import React from 'react';
import { Lock, Sparkles, Trophy, CheckCircle2, Flame } from 'lucide-react';
import './SideQuestStatusCard.css';
import { useUser } from '../context/UserContext';

/**
 * SideQuestStatusCard Component
 * 
 * Props:
 * @param {boolean} locked - True if Side Quests are locked
 * @param {number} progress - Progress towards unlocking (0, 1, 2, or 3)
 * @param {number} remainingNeeded - Quizzes needed to unlock (e.g., 3, 2, 1, 0)
 * @param {number} dailyRegularQuizzes - Tally of standard quizzes done today
 * @param {number} dailySideQuests - Tally of side quests done today
 * @param {function} onStartQuest - Callback when student clicks 'Play Side Quest'
 */
export default function SideQuestStatusCard({
  dailyRegularQuizzes = 0,
  dailySideQuests = 0,
  onStartQuest
}) {
  const TOTAL_REQUIRED = 3;
  const { user } = useUser();
  const progressPercentage = Math.min(100, (user.quests.progress / TOTAL_REQUIRED) * 100);

  return (
    <div className={`sq-card ${user.quests.questsLocked ? 'sq-card--locked' : 'sq-card--unlocked'}`}>

      {/* Header Section */}
      <div className="sq-card__header">
        <div className="sq-card__title-group">
          <span className="sq-card__icon">🚀</span>
          <div>
            <h3 className="sq-card__title">Side Quests</h3>
          </div>
        </div>

        {/* Lock Status Pill */}
        {!user.quests.questsLocked ? (
          <span className="sq-pill sq-pill--unlocked">
            <Sparkles size={13} /> UNLOCKED
          </span>
        ) : (
          <span className="sq-pill sq-pill--locked">
            <Lock size={13} /> LOCKED
          </span>
        )}
      </div>

      {/* Body Content */}
      {!user.quests.questsLocked ? (
        /* --- UNLOCKED STATE --- */
        <div className="sq-body sq-body--unlocked">
          <div className="sq-alert">
            <div className="sq-alert__content">
              <Trophy size={16} className="sq-alert__icon" />
              <span>Quests unlocked: {user.quests.questsUnlocked}</span>
            </div>
          </div>

          <button className="sq-button" onClick={onStartQuest}>
            <Sparkles size={16} /> Play Side Quest Now
          </button>
        </div>
      ) : (
        /* --- LOCKED STATE (PROGRESS TRACKER) --- */
        <div className="sq-body sq-body--locked">
          <div className="sq-progress-info">
            <span className="sq-progress-text">
              <strong className="sq-highlight">{user.quests.remainingNeeded}</strong> more quiz{user.quests.remainingNeeded > 1 ? 'zes' : ''} to unlock
            </span>
            <span className="sq-progress-count">{user.quests.progress} / {TOTAL_REQUIRED}</span>
          </div>

          {/* Progress Bar */}
          <div className="sq-progress-track">
            <div
              className="sq-progress-bar"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          {/* Step Indicators */}
          {/* <div className="sq-steps">
            {[1, 2, 3].map((step) => {
              const isDone = user.quests.progress >= step;
              return (
                <div key={step} className="sq-step">
                  <div className={`sq-step__circle ${isDone ? 'sq-step__circle--done' : ''}`}>
                    {isDone ? <CheckCircle2 size={14} /> : step}
                  </div>
                  <span className={`sq-step__label ${isDone ? 'sq-step__label--done' : ''}`}>
                    Q {step}
                  </span>
                </div>
              );
            })}
          </div> */}
        </div>
      )}
      {/* Footer Daily Tally */}
      <div className="sq-card__footer">
        <span className="sq-tally-label">
          <Flame size={12} className="sq-tally-icon" /> Today's Tally
        </span>
        <div className="sq-tally-counts">
          <span>Quizzes: <strong>{user.quests.dailyRegularQuizzes}</strong></span>
          <span className="sq-divider">·</span>
          <span>Quests: <strong>{user.quests.dailySideQuests}</strong></span>
        </div>
      </div>
    </div>
  );
}